import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
import { TagLine } from './TagLine';
import ItemOverlay from './ItemOverlay';
import { Tag } from 'types/event';
import { Classroom } from 'types/classroom';
import findIndex from 'lodash-es/findIndex';
import changeItemIndex from 'utils/arrays/changeItemAtIndex';
import useReorderTags from 'queries/tags/useReorderTags';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { ColorPaletteConfig } from 'containers/App/context/useColorPalette';

interface Props {
  onAddTagToClassroom: (p: {
    tagId: string;
    classroomId: string;
  }) => Promise<any>;
  onChange: (v?: Tag) => void;
  options: Tag[];
  currentClassrooms: Classroom[];
  setCurrentTag: (v: string | undefined) => void;
  currentTagOpen: string | undefined;
  disableEdit?: boolean;
  colorPalette: ColorPaletteConfig;
}

export function TagList(props: Props) {
  const {
    onAddTagToClassroom,
    options,
    currentClassrooms,
    onChange,
    setCurrentTag,
    currentTagOpen,
    disableEdit,
    colorPalette,
  } = props;
  const { schoolyear, school } = useSchoolSchoolyear();
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor),
  );
  const [activeId, setActiveId] = useState(null);
  const reorderTags = useReorderTags({
    schoolyearId: schoolyear.id,
    schoolId: school.id,
  });

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) {
      return null;
    }
    const fromIndex = findIndex(options, d => d.id === active.id);
    const toIndex = findIndex(options, d => d.id === over.id);
    const tagsInNewOrder = changeItemIndex(options || [], fromIndex, toIndex);
    await reorderTags(tagsInNewOrder.map(d => d.id));
    setActiveId(null);
    return null;
  }

  async function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext
        items={options.map(d => d.id)}
        strategy={verticalListSortingStrategy}
      >
        <div>
          {options.map(col => (
            <SortableItem id={col.id} key={col.id}>
              <TagLine
                disableEdit={disableEdit}
                onAddTagToClassroom={onAddTagToClassroom}
                currentClassrooms={currentClassrooms}
                setCurrentTag={setCurrentTag}
                currentTagOpen={currentTagOpen}
                onChange={onChange}
                value={col}
                colorPalette={colorPalette}
              />
            </SortableItem>
          ))}
        </div>
      </SortableContext>
      {createPortal(
        <DragOverlay>
          {activeId ? (
            <ItemOverlay
              tw="opacity-40"
              id={activeId}
              schoolyearId={schoolyear.id}
              schoolId={school.id}
              tag={options.find(t => t.id === activeId)}
            />
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
